// @givmo/app-charity — the status of a claim still awaiting review (CPA-WEB10 / D-CP-36, re-homed
// from the dissolved dashboard by CPA-WEB40).
//
// With the Claim nav group retired, this card is the claimant's status surface — so it has to answer,
// without being clicked, everything the old "My claims" trip answered: which organization, why it is
// taking a person rather than a second, when it went in, and what happens next. It REPLACES the app
// grid on `/apps` while the caller has no organization yet (§9-1), and leads the page above the grid
// for the rare admin claiming a second one.
//
// VOCABULARY: nothing here names a tier. `TIER_2` is our word for the manual path; what the claimant
// needs is the true reason a person is reading their claim, which `claimReviewNote` reads off the
// claim's `reason` (CPA-WEB91) — the same note the claim page shows. The status sentence is
// `claimStatusDescription`, the same translated copy `/claims` renders, so the two surfaces cannot
// drift.
//
// ★ A PENDING CLAIM IS STRUCTURALLY TIER-2, and that is why the copy below makes no promise about
// outstanding work. Verified in `givmo-backend-python` at origin/development,
// `app/application/charity_claim_service.py` — the submit path has exactly two branches:
// `_submit_auto_verified` creates the request `tier=TIER_1, status=AUTO_VERIFIED` and approves it in
// the SAME transaction, and `_submit_tier2` creates it `tier=TIER_2, status=PENDING_REVIEW`. Nothing
// else writes `PENDING_REVIEW`, so no claim a client can read ever rests in any other pending state.
// The tier branching that remains here is tolerant-reader defensiveness, not a live variation.
//
// DATA CONTRACT: `home-claim-pending` (+ `-note`, `-submitted`, `-detail`).

import { Link } from '@tanstack/react-router';
import type { CharityClaimRequestOut } from '@givmo/api-client';
import { Card, CardBody, Icon, testIdProp } from '@givmo/design-system';
import { ArrowRight, Building2 } from 'lucide-react';
import { claimDetailPath } from './claim-routes';
import { formatClaimDate } from './claim-decode';
import { CLAIM_TIER, claimReviewNote, claimStatusDescription } from './claim-vocab';

const PREFIX = 'claim-pending-card';

export function ClaimPendingCard({ claim }: { claim: CharityClaimRequestOut }) {
  const reviewNote = claimReviewNote(claim);
  const submitted = formatClaimDate(claim.created_at);
  // A Tier-2 claim's detail page carries the document upload, so the action says so — it is a place
  // the claimant may still have work to do, not just a record to read.
  const isTier2 = claim.tier === CLAIM_TIER.tier2;

  return (
    <Card testId={PREFIX}>
      <CardBody className="flex flex-col gap-12">
        <div className="flex items-start gap-12">
          <span className="flex size-[40px] shrink-0 items-center justify-center rounded-lg bg-info-50 text-info-600">
            <Icon icon={Building2} size={20} aria-hidden />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
              We’re reviewing your claim for {claim.attested_legal_name || 'your organization'}
            </h2>
            <p className="mt-4 text-s13 leading-normal text-text-secondary">
              {claimStatusDescription(claim.status)}
            </p>
            {reviewNote !== null ? (
              <p
                className="mt-6 text-s13 leading-normal text-text-tertiary"
                {...testIdProp(`${PREFIX}-note`)}
              >
                {reviewNote}
              </p>
            ) : null}
            {submitted !== null ? (
              <p
                className="mt-6 text-s12 text-text-tertiary"
                {...testIdProp(`${PREFIX}-submitted`)}
              >
                Submitted {submitted}
              </p>
            ) : null}
            {/* ★ The promise stops at the email, deliberately. A pending claim is structurally
                Tier-2 (see the header), and a Tier-2 claim needs an IRS determination letter that may
                not be uploaded yet; `CharityClaimRequestOut` carries no document state, so the card
                cannot know whether that work is outstanding. What IS true regardless is the email. */}
            <p className="mt-6 text-s13 leading-normal text-text-secondary">
              We’ll email you as soon as there’s an update.
            </p>
          </div>
        </div>
        <div className="flex justify-end">
          <Link
            to={claimDetailPath(claim.id)}
            className="inline-flex h-32 items-center gap-4 rounded-md px-12 font-sans text-s13 font-w600 tracking-givmo text-brand-700 outline-none transition-colors hover:bg-surface-hover focus-visible:shadow-ring"
            {...testIdProp(`${PREFIX}-detail`)}
          >
            {isTier2 ? 'View claim and documents' : 'View claim'}
            <Icon icon={ArrowRight} size={14} aria-hidden />
          </Link>
        </div>
      </CardBody>
    </Card>
  );
}
